import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/api";
import { toast } from "sonner";

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
};

export default function SignIn() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    const target = user.role === "admin" ? "/admin" : (location.state?.from || "/account");
    return <Navigate to={target} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(identifier, password);
      toast.success(`Welcome back, ${u.name?.split(" ")[0] || "rider"}.`);
      if (u.must_change_password) {
        navigate("/force-change-password");
      } else {
        navigate(u.role === "admin" ? "/admin" : (location.state?.from || "/account"));
      }
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="signin-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md border border-white/10 p-8 lg:p-10 bg-[#0E0E0E]">
        <img src={LOGO_URL} alt="MileForge" className="h-14 w-auto object-contain mb-3 -ml-3" />
        <div className="overline mb-1">Welcome Back</div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Sign In</h1>
        <p className="text-sm text-white/55 font-body mt-2">Track orders. Save addresses. Sync your wishlist.</p>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label className="overline block mb-2">Email or Phone</label>
            <input data-testid="signin-email" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@email.com or phone number" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body" />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input data-testid="signin-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body" />
          </div>
          {error && <div data-testid="signin-error" className="text-sm text-[#D96A1B] font-body">{error}</div>}
          <button data-testid="signin-submit" disabled={loading} className="btn-orange w-full justify-center">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/forgot-password" data-testid="link-to-forgot" className="text-xs font-display tracking-widest uppercase text-white/55 hover:text-[#D96A1B]">Forgot password?</Link>
        </div>

        <p className="mt-6 text-sm font-body text-white/60 text-center">
          New to MileForge? <Link to="/signup" data-testid="link-to-signup" className="text-[#D96A1B] hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
